import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import 'react-toastify/dist/ReactToastify.css';

import api from '../../api';
import useGetAllCustomers from '../../hooks/useGetAllCustomers';
import useGetDevicesForCustomer from '../../hooks/useGetDevicesForCustomer';
import { useHttpGetGatewaysAmongDevices } from '../../hooks';
import { waitingGatewayDevices } from './waitingGateways';
import useFacilityDevices from '../../hooks/useFacilityDevices';
import CustomerLookup from './CustomerLookup';
import CustomerInfo from './CustomerInfo';
import FacilityMap from './FacilityMap';
import DeviceLogs from './DeviceLogs';
import GatewaySection from './GatewaySection';
import CustomerAppView from './CustomerAppView';
import Loader from '../../components/Loader';

const TOAST_OPTIONS = {
    position: 'top-center',
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const CustomerDetailsSafetyWrapper = () => {
    const { data: customers } = useGetAllCustomers();
    const customerId = decodeURIComponent(window.location.pathname.split('/')[2]);
    const { isReady: devicesReady, data: devices, reload: reloadDevices } = useGetDevicesForCustomer(customerId, true);

    const deviceIds = useMemo(() => (devicesReady ? devices.map(d => d.id) : []), [devicesReady, devices]);
    const { isReady: gatewaysReady, data: gatewayIds } = useHttpGetGatewaysAmongDevices(deviceIds);

    const ready = customers.length && devicesReady && gatewaysReady;
    const gatewayDeviceIds = gatewaysReady && gatewayIds ? gatewayIds.data.device_ids : [];
    const gatewayDevices = devicesReady && gatewaysReady ? devices.filter(d => gatewayDeviceIds.includes(d.id)) : [];

    if (!ready) {
        return <Loader />;
    }

    return (
        <CustomerDetails
            customers={customers}
            devices={devices}
            gatewayDevices={gatewayDevices}
            gatewayDeviceIds={gatewayDeviceIds}
            reloadDevices={reloadDevices}
            customerId={customerId}
        />
    );
};

const CustomerDetails = ({ customers, devices, gatewayDevices, gatewayDeviceIds = [], reloadDevices, customerId }) => {
    const history = useHistory();

    const [facilitiesList, setFacilitiesList] = useState([]);
    const { data: facilityDevices, reload: reloadFacilityDevices } = useFacilityDevices(facilitiesList);
    const waitingGateways = waitingGatewayDevices(facilityDevices, gatewayDeviceIds);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [selectedThemeMode, setSelectedThemeMode] = useState('light');
    const [loading, setLoading] = useState(true);
    const [webAppLink, setWebAppLink] = useState('');
    const [onboardingAppLink, setOnboardingAppLink] = useState('');

    useEffect(() => {
        if (customerId && customers.length) {
            const customer = customers.find(c => c.customer_id === customerId);

            if (customer) {
                loadCustomerData(customer);
            } else {
                toast.error('Could not find customer with this number!', {
                    ...TOAST_OPTIONS,
                    position: 'top-right',
                    autoClose: 900,
                    onClose: () => history.push('/devicesdetail'),
                });
            }
        }
    }, [customers, customerId, history]);

    const reloadFacilities = useCallback(async () => {
        const customerFacilitiesRes = (await api.customer_facilities(customerId)).data;
        setFacilitiesList(customerFacilitiesRes);
    }, [customerId]);

    const loadCustomerData = useCallback(
        async customer => {
            try {
                setLoading(true);
                const customerFacilitiesRes = (await api.customer_facilities(customer.customer_id)).data;
                const firstFacility = customerFacilitiesRes[0];
                setFacilitiesList(customerFacilitiesRes);

                if (!selectedFacility && firstFacility) {
                    setSelectedFacility(firstFacility.facility_id);
                }

                await Promise.all([
                    loadWebAppLink(customer, selectedFacility || firstFacility?.facility_id, selectedLanguage, selectedThemeMode),
                    loadOnboardingAppLink(customer, selectedFacility || firstFacility?.facility_id, selectedLanguage, selectedThemeMode),
                ]);

                setLoading(false);
            } catch (error) {
                toast.error('Could not load customer, please try again!', TOAST_OPTIONS);
                setLoading(false);
            }
        },
        [selectedFacility, selectedLanguage, selectedThemeMode]
    );

    const loadWebAppLink = useCallback(async (customer, facilityId, language, themeMode) => {
        try {
            const { link } = (await api.customer_web_app_link(customer.customer_id)).data;
            setWebAppLink(`${link}&facility=${facilityId}&language=${language}&userAppTheme=${themeMode}`);
        } catch {
            toast.error('Something went wrong while loading the customer app for this facility!', {
                ...TOAST_OPTIONS,
                position: 'top-right',
                autoClose: 900,
            });
        }
    }, []);

    const loadOnboardingAppLink = useCallback(async (customer, facilityId, language, themeMode) => {
        try {
            const onboardingAppLinkData = (await api.onboarding_app_link(customer.customer_id)).data.link;
            setOnboardingAppLink(
                `${onboardingAppLinkData.replace('dummy', 'add-device')}&facility=${facilityId}&language=${language}&userAppTheme=${themeMode}`
            );
        } catch (error) {
            toast.error('Something went wrong while loading the onboarding app for this facility!', {
                ...TOAST_OPTIONS,
                position: 'top-right',
                autoClose: 900,
            });
        }
    }, []);

    const handleFacilityChange = useCallback(
        value => {
            if (!value) {
                return;
            }

            setSelectedFacility(value);
            const customer = customers.find(c => c.customer_id === customerId);
            if (customer) {
                loadWebAppLink(customer, value, selectedLanguage, selectedThemeMode);
                loadOnboardingAppLink(customer, value, selectedLanguage, selectedThemeMode);
            }
        },
        [customers, customerId, selectedLanguage, selectedThemeMode, loadWebAppLink, loadOnboardingAppLink]
    );

    const handleLanguageChange = useCallback(
        value => {
            if (!value) {
                return;
            }

            setSelectedLanguage(value);
            const customer = customers.find(c => c.customer_id === customerId);
            if (customer) {
                loadWebAppLink(customer, selectedFacility, value, selectedThemeMode);
                loadOnboardingAppLink(customer, selectedFacility, value, selectedThemeMode);
            }
        },
        [customers, customerId, selectedFacility, selectedThemeMode, loadWebAppLink, loadOnboardingAppLink]
    );

    const handleThemeModeChange = useCallback(
        value => {
            if (!value) {
                return;
            }

            setSelectedThemeMode(value);
            const customer = customers.find(c => c.customer_id === customerId);
            if (customer) {
                loadWebAppLink(customer, selectedFacility, selectedLanguage, value);
                loadOnboardingAppLink(customer, selectedFacility, selectedLanguage, value);
            }
        },
        [customers, customerId, selectedFacility, selectedLanguage, loadWebAppLink, loadOnboardingAppLink]
    );

    const handleBackClick = () => {
        history.goBack();
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <ToastContainer />
            <div className="customer-details">
                <div className="customer-details__header">
                    <button className="customer-details__header--back-btn" onClick={handleBackClick}>
                        &#60; Back
                    </button>
                    <div className="customer-details__header--search-customers">
                        <CustomerLookup searchPageActive={false} />
                    </div>
                </div>

                <div className="customer-details__main-container">
                    <div className="customer-details__first-section">
                        <CustomerInfo customerId={customerId} facilities={facilitiesList} onSuccess={reloadFacilities} />

                        {facilitiesList.length > 0 && <FacilityMap facilities={facilitiesList} />}

                        <DeviceLogs devices={devices} />

                        <GatewaySection
                            gatewayDevices={gatewayDevices}
                            waitingGateways={waitingGateways}
                            customerId={customerId}
                            facilities={facilitiesList}
                            onSuccess={() => {
                                reloadFacilities();
                                // A held onboarding adds its placeholder device at once.
                                reloadFacilityDevices();
                                if (reloadDevices) {
                                    reloadDevices();
                                }
                            }}
                        />
                    </div>

                    <CustomerAppView
                        facilities={facilitiesList}
                        selectedFacility={selectedFacility}
                        selectedLanguage={selectedLanguage}
                        selectedThemeMode={selectedThemeMode}
                        onFacilityChange={handleFacilityChange}
                        onLanguageChange={handleLanguageChange}
                        onThemeModeChange={handleThemeModeChange}
                        webAppLink={webAppLink}
                        onboardingAppLink={onboardingAppLink}
                    />
                </div>
            </div>
        </>
    );
};

export { CustomerDetailsSafetyWrapper as CustomerDetails };
export default CustomerDetailsSafetyWrapper;
